import { useEffect, useMemo, useState } from "react"

import * as AdrDomain from "@/domain/adr"
import { useServices } from "@/hooks/useServices"
import { DataContext, getInitialState } from "@/pages/TriagePage/hooks/useData"

import { getDemoData } from "@/pages/TriagePage/application/demo"

export const DataProvider = (props: React.PropsWithChildren) => {
  const [state, setState] = useState(getInitialState())
  const { auth } = useServices()
  const isDemo = auth.isDemo()

  useEffect(() => {
    if (isDemo) {
      const fn = (e: KeyboardEvent) => {
        const mapping = {
          "1": "b232c689-e0c2-45f8-8bc7-9db62d180e7e",
          "2": "d1e2f3a4-b5c6-7890-d1e2-f3a4b5c67890",
          "3": "b999d893-e1a2-5cd6-9fe8-7ec99d381e99",
          "4": "12345678-abcd-4321-efgh-987654321abc",
          "5": "e7c1a4b2-3d5f-4a6b-8c9d-1e2f3a4b5c6d",
          "6": "f3a9c2d1-7b4e-4c8a-9e1f-2d6b5a8c4e71",
          "7": "7c4e1a9b-2d6f-4b8a-9e3c-5f1d7a2b8c4e",
          "8": "a8f3b1c2-d4e5-4f6a-9b7c-8d0e1f2a3b4c"
        } as Record<string, string>

        if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return
        if (
          e.target instanceof HTMLElement &&
          e.target.closest("input, textarea, [contenteditable='true']")
        )
          return

        if (e.key === "0")
          return setState((prev) => {
            const incidents = getDemoData().filter(
              (d) =>
                !prev.incidents.find(
                  ({ incident }) =>
                    incident.general.id === d.incident.general.id
                )
            )

            if (incidents.length === 0) return prev

            const nextData = incidents.reduce(
              (acc, curr) => {
                const type = AdrDomain.getTypeDTO(
                  AdrDomain.getIncidentDTO(curr)
                )
                const environment = AdrDomain.getEnvironmentDTO(
                  AdrDomain.getIncidentDTO(curr)
                )

                if (curr.dismissDetails?.dismissedAt) return acc

                acc.rules[type] = (acc.rules[type] || 0) + 1
                acc.environments[environment] =
                  (acc.environments[environment] || 0) + 1

                return acc
              },
              {
                rules: { ...prev.rules },
                environments: { ...prev.environments }
              } as {
                rules: Record<string, number>
                environments: Record<string, number>
              }
            )

            return {
              ...prev,
              ...nextData,
              incidents
            }
          })

        const id = mapping[e.key]
        const record = id
          ? getDemoData().find((d) => d.incident.general.id === id)
          : null

        if (!record) return

        const nextRecord = { ...record }
        const date = new Date().toISOString()

        nextRecord.incident.general.createdAt = date
        nextRecord.incident.general.detectedAt = date

        setState((prev) => {
          const isExist = prev.incidents.findIndex(
            (currentRecord) =>
              AdrDomain.getIdDTO(AdrDomain.getIncidentDTO(record)) ===
              AdrDomain.getIdDTO(AdrDomain.getIncidentDTO(currentRecord))
          )

          if (isExist !== -1) return prev

          const type = AdrDomain.getTypeDTO(AdrDomain.getIncidentDTO(record))
          const environment = AdrDomain.getEnvironmentDTO(
            AdrDomain.getIncidentDTO(record)
          )

          return {
            ...prev,
            rules: {
              ...prev.rules,
              [type]: (prev.rules[type] || 0) + 1
            },
            environments: {
              ...prev.environments,
              [environment]: (prev.environments[environment] || 0) + 1
            },
            incidents: [nextRecord, ...prev.incidents]
          }
        })
      }

      document.addEventListener("keydown", fn)

      return () => {
        document.removeEventListener("keydown", fn)
      }
    }
  }, [isDemo])

  const value = useMemo(
    () => ({
      ...state,
      toggleDetails: () =>
        setState((prev) => ({ ...prev, details: !prev.details }))
    }),
    [state]
  )

  return (
    <DataContext.Provider value={value}>{props.children}</DataContext.Provider>
  )
}
